import { get, isEmpty } from 'lodash';
import { toQuery, nodeWithArgs, node, inlineFragment, enumeration } from 'common/util/queryHelper';
import { queryGraphQL } from 'common/util/query';
import { ACTIONS, TASK_TYPES } from 'workflow/constants/newInbox';
import { addApplicationMessages, addApplicationMessage } from 'common/actions/applicationMessagesActions';

const fetchDocumentViewerByTaskId = taskId => async dispatch => {
    const query = nodeWithArgs('taskDetails', {
        taskId
    }, [
        '__typename',
        'id',
        'assignee',
        nodeWithArgs('nextTask', {
            sortBy: 'created', sortOrder: 'desc'
        }),
        inlineFragment('SDFATask', [
            'title',
            'name',
            'sentDate',
            'dueDate',
            'reminderDate',
            'actionName',
            'description',
            'finished',
            'deleted',
            'status',
            node('signingStatement', [
                'id',
                'statement'
            ]),
            node('document', [
                'id',
                'name',
                'uploadDate',
                'institutionId',
                'documentIdentifier',
                node('signatures', [
                    'dateSigned'
                ]),
                node('uploadUser', [
                    'displayName',
                    'emailAddress'
                ])
            ])
        ])
    ]);

    const { errors, data } = await queryGraphQL(toQuery(query));
    if (data) {
        const document = get(data, 'taskDetails.document', null);
        if (document) {
            dispatch(setTaskDetail(data.taskDetails));
        } else {
            dispatch(addApplicationMessage('error', 'No document is associated with this task.'));
        }
    } else {
        dispatch(addApplicationMessages('error', errors.map(error => error.message)));
        dispatch(setTaskDetailFetching(false));
    }
};

const loadSdfaSentItemTaskDetail = (workflowId, userPermission, binderId) => async dispatch => {
    let resolver, queryString;

    if (userPermission) {
        resolver = 'workflowsByProcessVariable';
        queryString = {
            processInstanceId: workflowId,
            processVariableName: 'binderId',
            processVariableValue: binderId,
        };
    } else {
        resolver = 'myWorkflows';
        queryString = {
            id: workflowId,
        };
    }
    dispatch(setTaskDetailFetching(true));
    const query = toQuery(nodeWithArgs(resolver, queryString, [
        'id',
        'title',
        'name',
        'sentDate',
        'dueDate',
        'author',
        'reminderDate',
        'actionName',
        'status',
        'description',
        node('recipients', [
            'sub',
            'displayName',
            'emailAddress',
        ]),
        node('tasks', [
            'id',
            'assignee',
            'finished',
            'deleted',
        ]),
        node('document', [
            'id',
            'name',
            'uploadDate',
            'institutionId',
            'documentIdentifier',
            nodeWithArgs('renditions', {
                type: enumeration('VIEW'),
            }, [
                'objectName',
                'containerId',
                'prettyFileName',
                'fileExt',
                'mimeType',
                'fileSize',
                'filePurpose',
                'createdDate',
                'type',
                node('downloadRequest', [
                    'tempUrl'
                ])
            ]),
            node('signatures', [
                'dateSigned',
            ]),
            node('uploadUser', [
                'sub',
                'displayName',
                'emailAddress',
            ]),
            node('documentType', [
                'id',
                'displayName',
            ]),
        ]),
    ]));
    const { errors, data } = await queryGraphQL(query);

    dispatch(setTaskDetailFetching(false));

    if (errors) {
        dispatch(addApplicationMessages('error', errors.map(e => e.message)));
    } else {
        const responseData = userPermission ? data.workflowsByProcessVariable : data.myWorkflows;


        const document = get(responseData[0], 'document', null);
        const recipients = get(responseData[0], 'recipients', null);
        const tasks = get(responseData[0], 'tasks', null);
        if (document) {
            dispatch(setTaskDetail({ document, recipients, tasks }));
        } else {
            dispatch(addApplicationMessage('error', 'No document is associated with this task.'));
        }
    }
};

const fetchEventDetailByTaskId = taskId => async dispatch => {
    const query = nodeWithArgs('signatureRequest', {
        signatureRequestId: taskId
    }, [
        'id',
        'status',
        node('createdBy', [
            'id',
            'displayName',
        ]),
        node('requestedFrom', [
            'id',
            'displayName',
            'emailAddress',
        ]),
        node('event', [
            'id',
            'eventType',
            'monitorVisitType',
            'dateOfVisit',
            'institutionId',
            'irbApprovalDate',
            'icfType',
            'icfLanguage',
            'reconsentRequired',
            'sponsorVersion',
            'siteVersion',
            'protocolVersion',
            'summaryOfChanges',
            node('monitor', [
                'displayName',
            ]),
            node('binder', [
                'id',
                'status'
            ])
        ]),
        node('customEvent', [
            'id',
            'institutionId',
            node('eventType', [
                'id',
                'displayName',
                'name',
            ]),
            node('primaryDate', [
                'name',
                'displayName',
                'value',
                'valueType',
                'view'
            ]),
            node('fields', [
                'id',
                'name',
                'displayName',
                'value',
                'valueType',
                'view'
            ]),
        ]),
        'sentDate',
        'binderName',
    ]);

    const { errors, data } = await queryGraphQL(toQuery(query));

    if (data) {
        const { signatureRequest } = data;
        dispatch(setTaskDetail(signatureRequest));
    } else {
        dispatch(addApplicationMessages('error', errors.map(error => error.message)));
        dispatch(setTaskDetailFetching(false));
    }
};

const fetchDOATaskDetailByTaskId = doaTaskId => async dispatch => {
    const query = toQuery(
        nodeWithArgs(
            'signatureRequest',
            {
                signatureRequestId: doaTaskId,
            },
            [
                'id',
                'status',
                node('createdBy', ['id', 'displayName']),
                node('requestedFrom', ['id', 'displayName', 'emailAddress']),
                node('delegation', [
                    'id',
                    'binderId',
                    node('user', ['id', 'displayName', 'emailAddress']),
                    node('rolesManagement', ['id', 'name']),
                    'taskIds',
                    'startDate',
                    'endDate',
                    node('tasks', ['id', 'taskName', 'taskIndex']),
                ]),
                'sentDate',
                'binderName',
                'statement'
            ]
        )
    );

    const { errors, data } = await queryGraphQL(query);

    if (errors) {
        dispatch(addApplicationMessages('error', 'Failed to load the DOA task'));
        dispatch(setTaskDetailFetching(false));
    } else {
        const { signatureRequest } = data;
        dispatch(setTaskDetail(signatureRequest));
    }
};

const clearTaskDetail = () => ({
    type: ACTIONS.RESET_STATE_TASK_DETAIL
});

const setTaskDetailFetching = taskDetailFetching => ({
    type: ACTIONS.SET_TASK_DETAIL_FETCHING,
    payload: taskDetailFetching
});

const setTaskDetail = taskDetail => ({
    type: ACTIONS.SET_TASK_DETAIL,
    payload: taskDetail
});

const fetchTaskDetail = task => async dispatch => {
    if (!isEmpty(task)) {
        dispatch(setTaskDetailFetching(true));
        const { typeName, taskId, id } = task;
        if (typeName === TASK_TYPES.SDFA) {
            dispatch(fetchDocumentViewerByTaskId(id));
        } else if (typeName === TASK_TYPES.EVENT) {
            dispatch(fetchEventDetailByTaskId(taskId));
        } else if (typeName === TASK_TYPES.DOA) {
            dispatch(fetchDOATaskDetailByTaskId(taskId));
        }
    }
};

const taskDetailActions = {
    fetchTaskDetail,
    clearTaskDetail,
    loadSdfaSentItemTaskDetail
};

export default taskDetailActions;
